<template>
  <section class="management-guidance" :class="[`management-guidance--${status}`, `management-guidance--${design}`]" aria-label="Comment from Management">
    <div class="management-guidance__heading">
      <div class="management-guidance__title">
        <h3>❤️ Comment from Management</h3>
        <button
          v-if="authorDetails"
          class="management-guidance__info"
          type="button"
          aria-label="Comment author and date"
          :aria-expanded="showAuthorDetails"
          :aria-controls="`management-guidance-info-${leadId}`"
          @click="showAuthorDetails = !showAuthorDetails"
        >
          <svg viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
            <circle cx="10" cy="10" r="8" stroke="currentColor" stroke-width="1.5" />
            <path d="M10 9v5M10 6.2h.01" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </button>
      </div>
      <LeadManagementGuidanceIcon :status="status" :design="design" />
    </div>
    <p v-if="authorDetails" v-show="showAuthorDetails" :id="`management-guidance-info-${leadId}`" class="management-guidance__metadata">{{ authorDetails }}</p>
    <div v-if="loading && !guidance" class="management-guidance__message">Loading management comment…</div>
    <template v-else>
      <p v-if="status === 'commented'" class="management-guidance__comment">{{ guidance.comment }}</p>
      <p v-else class="management-guidance__message">
        {{ status === 'not_required' ? 'Management comment not needed.' : 'No management decision yet.' }}
      </p>
      <div v-if="canEdit" class="management-guidance__controls">
        <template v-if="editing">
          <label class="management-guidance__label" :for="`management-comment-${leadId}`">Advice for the sales manager</label>
          <textarea :id="`management-comment-${leadId}`" v-model="draft" rows="4" maxlength="5000" placeholder="What should the sales manager do with this client?" />
          <div class="management-guidance__buttons">
            <app-button :design="design" size="small" :disabled="saving || !draft.trim()" @click.native="save('commented')">{{ saving ? 'Saving…' : 'Publish comment' }}</app-button>
            <app-button :design="design" size="small" theme="dark" :disabled="saving" @click.native="editing = false">Cancel</app-button>
          </div>
        </template>
        <div v-else class="management-guidance__buttons">
          <app-button :design="design" size="small" @click.native="startEditing">{{ status === 'commented' ? 'Replace comment' : 'Add comment' }}</app-button>
          <app-button :design="design" size="small" theme="dark" :disabled="saving" @click.native="save('not_required')">{{ design === 'legacy' ? 'Comment not needed' : 'Not needed' }}</app-button>
        </div>
      </div>
      <p v-if="error" class="management-guidance__error" role="alert">{{ error }} <button type="button" @click="load">Refresh</button></p>
    </template>
  </section>
</template>

<script>
import { getLeadManagementGuidance, patchLeadManagementGuidance } from '@/http'
import { format, parseISO } from 'date-fns'
import LeadManagementGuidanceIcon from './LeadManagementGuidanceIcon.vue'

export default {
  name: 'LeadManagementGuidance',
  components: { LeadManagementGuidanceIcon },
  props: {
    leadId: { type: Number, required: true },
    initial: { type: Object, default: null },
    design: { type: String, default: 'new', validator: value => ['legacy', 'new'].includes(value) }
  },
  data() {
    return {
      guidance: this.initial,
      draft: '',
      editing: false,
      showAuthorDetails: false,
      loading: false,
      saving: false,
      error: '',
      generation: 0
    }
  },
  computed: {
    status() {
      return this.guidance ? this.guidance.status : 'pending'
    },
    authorDetails() {
      if (!this.guidance || !this.guidance.added_by) return ''
      const name = this.guidance.added_by.name
      const date = this.formatDate(this.guidance.updated_at)
      return `Added by ${name}${date ? ` · ${date}` : ''}`
    },
    canEdit() {
      const roles = ['sales_supervisor', 'general_manager', 'marketing_supervisor']
      return (this.$store.state.userGroups || []).some(group => roles.includes(group.code))
    }
  },
  watch: {
    leadId() {
      this.guidance = null
      this.editing = false
      this.showAuthorDetails = false
      this.load()
    }
  },
  created() {
    this.load()
  },
  beforeDestroy() {
    this.generation += 1
  },
  methods: {
    formatDate(value) {
      if (!value) return ''
      const date = parseISO(value)
      return Number.isNaN(date.getTime()) ? '' : format(date, 'd MMMM yyyy, HH:mm')
    },
    startEditing() {
      this.draft = this.guidance ? this.guidance.comment : ''
      this.error = ''
      this.editing = true
    },
    async load() {
      const generation = ++this.generation
      this.loading = true
      try {
        const { data } = await getLeadManagementGuidance(this.leadId)
        if (generation !== this.generation) return
        this.guidance = data
        this.error = ''
      } catch (error) {
        if (generation === this.generation) this.error = 'Could not load the latest management decision.'
      } finally {
        if (generation === this.generation) this.loading = false
      }
    },
    async save(status) {
      if (this.saving) return
      this.saving = true
      this.error = ''
      try {
        const { data } = await patchLeadManagementGuidance(this.leadId, {
          status,
          comment: status === 'commented' ? this.draft : '',
          expected_revision: this.guidance ? this.guidance.revision : 0
        })
        this.guidance = data
        this.editing = false
        this.$emit('changed', data)
      } catch (error) {
        const details = error.response && error.response.data && error.response.data.details
        this.error = details || 'Could not save the management decision.'
        await this.load()
        if (error.response && error.response.status === 400) {
          this.error = 'The management decision changed. Review the latest version before saving.'
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.management-guidance {
  min-width: 0;
  padding: 15px 16px;
  border: 1px solid #dfe4dc;
  border-left: 4px solid #929b9e;
  border-radius: 7px;
  background: #fff;
  color: #344749;
}
.management-guidance--commented { border-left-color: #147a55; }
.management-guidance--not_required { border-left-color: #c53b4b; }
.management-guidance__heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.management-guidance__title { display: flex; align-items: center; gap: 7px; min-width: 0; }
.management-guidance h3 { margin: 0; color: #1f3134; font-size: 15px; }
.management-guidance__info { display: inline-flex; flex: none; align-items: center; justify-content: center; width: 16px; height: 16px; padding: 0; border: 0; background: none; color: #929b9e; cursor: pointer; }
.management-guidance__info:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; border-radius: 50%; }
.management-guidance__metadata { margin: 10px 0 0; color: #738186; font-size: 13px; line-height: 1.4; }
.management-guidance__comment { margin: 12px 0 0; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 16px; }
.management-guidance__message { margin: 12px 0 0; color: #738186; font-size: 13px; }
.management-guidance__controls { margin-top: 12px; }
.management-guidance__label { display: block; margin-bottom: 6px; font-size: 13px; font-weight: 600; }
.management-guidance textarea { display: block; width: 100%; box-sizing: border-box; padding: 9px; border: 1px solid #ccd5d2; border-radius: 5px; resize: vertical; font: inherit; }
.management-guidance__buttons { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 9px; }
.management-guidance__error button { padding: 0; border: 0; background: none; color: inherit; text-decoration: underline; cursor: pointer; }
.management-guidance__error { color: #b73430; font-size: 13px; }
.management-guidance--legacy { border-color: #e1e1e1; color: #181818; font-size: 14px; }
.management-guidance--legacy.management-guidance--commented { border-left-color: #77b017; }
.management-guidance--legacy.management-guidance--not_required { border-left-color: #dd4e4e; }
.management-guidance--legacy h3 { color: #181818; font-size: 15px; font-weight: 500; }
.management-guidance--legacy .management-guidance__comment { font-size: 14px; line-height: 1.5; }
.management-guidance--legacy .management-guidance__message { color: #777; font-size: 14px; }
.management-guidance--legacy .management-guidance__metadata { color: #777; }
.management-guidance--legacy textarea { border-color: #d4d4d4; }
</style>
